import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Plus, Trash2, Camera } from "lucide-react";
import { api } from "@/lib/auth";
import { toast } from "sonner";

export default function AddRecipe() {
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    nombre: "",
    descripcion_corta: "",
    categoria: "Platos Caseros y Vegetarianos",
    festividad: "",
    origen: "",
    porciones: "",
    tiempo_preparacion: "",
    tiempo_coccion: "",
    dificultad: "Media",
    ingredientes: [""],
    instrucciones: [""],
    notas: "",
    photo_data_url: "",
  });

  useEffect(() => { api().get("/meta").then((res) => setMeta(res.data)); }, []);

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("La foto excede 5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((F) => ({ ...F, photo_data_url: reader.result }));
    reader.readAsDataURL(f);
  };

  const setItem = (key, idx, value) => {
    setForm((F) => {
      const arr = [...F[key]]; arr[idx] = value; return { ...F, [key]: arr };
    });
  };
  const addItem = (key) => setForm((F) => ({ ...F, [key]: [...F[key], ""] }));
  const delItem = (key, idx) => setForm((F) => ({ ...F, [key]: F[key].filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = {
      ...form,
      ingredientes: form.ingredientes.filter((s) => s.trim()),
      instrucciones: form.instrucciones.filter((s) => s.trim()),
      festividad: form.festividad || null,
      origen: form.origen || null,
    };
    if (!body.nombre.trim()) { toast.error("Falta el nombre"); setBusy(false); return; }
    if (body.ingredientes.length === 0) { toast.error("Agrega al menos un ingrediente"); setBusy(false); return; }
    if (body.instrucciones.length === 0) { toast.error("Agrega al menos un paso"); setBusy(false); return; }
    try {
      const res = await api().post("/recipes", body);
      toast.success("Receta agregada");
      nav(`/receta/${res.data.slug}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No pudimos guardarla");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen paper-texture iphone-safe-top">
      <header className="sticky top-0 z-30 bg-paper/95 backdrop-blur-md border-b border-line">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/cocina" className="p-2 rounded-full bg-cream border border-line"><ChevronLeft className="w-5 h-5 text-cocoa" /></Link>
          <h1 className="font-display italic text-cocoa text-xl">Agregar receta</h1>
          <div className="w-9 h-9" />
        </div>
      </header>

      <form onSubmit={submit} className="max-w-3xl mx-auto px-5 pt-6 pb-32">
        <label className="block mb-5">
          <span className="eyebrow">Nombre</span>
          <input data-testid="add-name-input" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} required maxLength={200} className="mt-2 w-full px-4 py-3 bg-cream border border-line rounded-md font-serif text-lg text-cocoa focus:outline-none focus:ring-2 focus:ring-amber-deep/40" placeholder="Ej: Sopa de la Bobe" />
        </label>

        <label className="block mb-5">
          <span className="eyebrow">Descripción corta</span>
          <input data-testid="add-desc-input" value={form.descripcion_corta} onChange={(e) => setForm({ ...form, descripcion_corta: e.target.value })} maxLength={300} className="mt-2 w-full px-4 py-3 bg-cream border border-line rounded-md font-serif text-base text-cocoa focus:outline-none focus:ring-2 focus:ring-amber-deep/40" placeholder="Una frase que la describa" />
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
          <label className="block">
            <span className="eyebrow">Categoría</span>
            <select data-testid="add-cat-select" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} className="mt-2 w-full px-4 py-3 bg-cream border border-line rounded-md font-serif text-base text-cocoa">
              {(meta?.categorias || []).map((c) => <option key={c.nombre}>{c.nombre}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="eyebrow">Festividad (opcional)</span>
            <select data-testid="add-fest-select" value={form.festividad} onChange={(e) => setForm({ ...form, festividad: e.target.value })} className="mt-2 w-full px-4 py-3 bg-cream border border-line rounded-md font-serif text-base text-cocoa">
              <option value="">— ninguna —</option>
              {(meta?.festividades || []).map((f) => {
                const name = typeof f === "string" ? f : (f?.nombre || "");
                if (!name) return null;
                return <option key={name} value={name}>{name}</option>;
              })}
            </select>
          </label>
          <label className="block">
            <span className="eyebrow">Porciones</span>
            <input data-testid="add-port-input" value={form.porciones} onChange={(e) => setForm({ ...form, porciones: e.target.value })} className="mt-2 w-full px-4 py-3 bg-cream border border-line rounded-md font-serif text-base text-cocoa" placeholder="4 personas" />
          </label>
          <label className="block">
            <span className="eyebrow">Dificultad</span>
            <select data-testid="add-dif-select" value={form.dificultad} onChange={(e) => setForm({ ...form, dificultad: e.target.value })} className="mt-2 w-full px-4 py-3 bg-cream border border-line rounded-md font-serif text-base text-cocoa">
              <option>Fácil</option><option>Media</option><option>Difícil</option>
            </select>
          </label>
          <label className="block">
            <span className="eyebrow">Tiempo de prep</span>
            <input data-testid="add-tprep-input" value={form.tiempo_preparacion} onChange={(e) => setForm({ ...form, tiempo_preparacion: e.target.value })} className="mt-2 w-full px-4 py-3 bg-cream border border-line rounded-md font-serif text-base text-cocoa" placeholder="20 min" />
          </label>
          <label className="block">
            <span className="eyebrow">Tiempo de cocción</span>
            <input data-testid="add-tcoc-input" value={form.tiempo_coccion} onChange={(e) => setForm({ ...form, tiempo_coccion: e.target.value })} className="mt-2 w-full px-4 py-3 bg-cream border border-line rounded-md font-serif text-base text-cocoa" placeholder="40 min" />
          </label>
        </div>

        <div className="mb-6">
          <p className="eyebrow mb-2">Foto (opcional)</p>
          <label className="block">
            <input data-testid="add-photo-input" type="file" accept="image/*" onChange={onPhoto} className="hidden" />
            <span className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-cream border border-line text-cocoa font-serif font-semibold cursor-pointer hover:border-amber-deep">
              <Camera className="w-4 h-4" /> {form.photo_data_url ? "Cambiar foto" : "Elegir foto"}
            </span>
          </label>
          {form.photo_data_url && <img src={form.photo_data_url} alt="" className="mt-3 rounded-md max-h-48" />}
        </div>

        {/* Ingredients */}
        <div className="mb-7">
          <p className="eyebrow mb-2">Ingredientes</p>
          <ul className="space-y-2">
            {form.ingredientes.map((ing, i) => (
              <li key={i} className="flex gap-2">
                <input data-testid={`add-ing-${i}`} value={ing} onChange={(e) => setItem("ingredientes", i, e.target.value)} placeholder="Ej: 2 tazas de harina" className="flex-1 px-3 py-2.5 bg-cream border border-line rounded font-serif text-cocoa" />
                {form.ingredientes.length > 1 && <button type="button" onClick={() => delItem("ingredientes", i)} className="p-2.5 text-cocoa-soft hover:text-amber-deep"><Trash2 className="w-4 h-4" /></button>}
              </li>
            ))}
          </ul>
          <button data-testid="add-ing-btn" type="button" onClick={() => addItem("ingredientes")} className="mt-3 inline-flex items-center gap-1 text-amber-deep font-serif italic font-semibold">
            <Plus className="w-4 h-4" /> Agregar ingrediente
          </button>
        </div>

        {/* Steps */}
        <div className="mb-7">
          <p className="eyebrow mb-2">Pasos</p>
          <ul className="space-y-3">
            {form.instrucciones.map((s, i) => (
              <li key={i} className="flex gap-2">
                <span className="font-display italic text-amber-deep text-2xl leading-none w-7 pt-1">{i + 1}</span>
                <textarea data-testid={`add-step-${i}`} value={s} onChange={(e) => setItem("instrucciones", i, e.target.value)} rows={2} placeholder="Describe el paso" className="flex-1 px-3 py-2.5 bg-cream border border-line rounded font-serif text-cocoa resize-y" />
                {form.instrucciones.length > 1 && <button type="button" onClick={() => delItem("instrucciones", i)} className="p-2.5 text-cocoa-soft hover:text-amber-deep"><Trash2 className="w-4 h-4" /></button>}
              </li>
            ))}
          </ul>
          <button data-testid="add-step-btn" type="button" onClick={() => addItem("instrucciones")} className="mt-3 inline-flex items-center gap-1 text-amber-deep font-serif italic font-semibold">
            <Plus className="w-4 h-4" /> Agregar paso
          </button>
        </div>

        <label className="block mb-7">
          <span className="eyebrow">Notas (opcional)</span>
          <textarea data-testid="add-notes-input" value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} rows={3} className="mt-2 w-full px-3 py-2.5 bg-cream border border-line rounded font-serif text-cocoa" placeholder="Trucos, sustituciones, recuerdos…" />
        </label>

        <div className="fixed bottom-0 left-0 right-0 bg-paper/95 backdrop-blur-md border-t border-line p-4 iphone-safe-bottom">
          <div className="max-w-3xl mx-auto">
            <button data-testid="add-submit-btn" type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-50">
              {busy ? "Guardando…" : "Guardar receta"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
